import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api, fullName, formatDate } from '../lib/api';
import { useAuth } from '../lib/auth';
import { etiquetaColegiado } from '../lib/medicos';
import { error as logError } from '../lib/logger';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Eye, FilePen, Trash2, Search, Tablet, CheckCircle2, Trash, FileCog, Syringe } from 'lucide-react';
import { toast } from 'sonner';
import { normalizaTexto } from '../lib/utils';
import PdfPreviewDialog from '../components/PdfPreviewDialog';
import { PatientCombobox } from '../components/PatientCombobox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ¿Hay ya una firma encima de este papel? La de la paciente, la del médico
// (firma desde la ficha) o la del anestesista (desde Quirófano): la misma
// regla que el servidor (`services.documentos_de_cita.esta_firmado`). Solo
// decide si se ofrece la papelera; quien impide borrar lo firmado es el
// servidor (23/9/2026: la papelera salía en todas las filas y el servidor
// borraba lo firmado sin mirar).
const tieneFirma = (c) => ['patient_signature_b64', 'doctor_signature_b64', 'anesthetist_signature_b64']
  .some((campo) => String(c?.[campo] || '').trim() !== '');

/** Lo que el servidor dice que anuló al borrar un consentimiento («· quitado
 *  de la tablet y anulado su enlace de casa»), o nada. Lo cuenta el SERVIDOR
 *  (`anulados` de DELETE /api/consents/{id}): la pantalla no lo supone. */
export function textoAnulados(anulados) {
  const partes = [];
  if (anulados?.tablet) partes.push('quitado de la tablet');
  if (anulados?.enlace) partes.push('anulado su enlace de firma de casa');
  return partes.length ? ` · ${partes.join(' y ')}` : '';
}

// El de anestesia con su jeringa y el resto con el documento. Antes eran los
// emojis de dormir y de la libreta, que en los equipos de la clínica salían
// como cuadrados (administración, 5/10/2026).
function IconoConsentimiento({ code }) {
  const Icono = code === 'anestesia' ? Syringe : FilePen;
  return <Icono className="w-3.5 h-3.5 shrink-0 text-[#2E6E5E]" aria-hidden="true" />;
}

/**
 * El diálogo de «Generar consentimiento». Lo abre esta pantalla (el buscador
 * de toda la clínica) y, desde el 21/10/2026, la pestaña «Consentimientos» de
 * la ficha del paciente, sin salir de ella.
 *
 * `pacienteFijo` (la ficha de la que se viene): el paciente no se elige ni se
 * vuelve a escribir. Va fijado en TODOS los que se generen, uno o varios de
 * golpe, y en los siguientes que se generen seguidos desde la misma ficha
 * (administración, 21/10/2026: «obliga a volver a escribir el nombre»). Antes
 * la ficha mandaba aquí con `?patient=` y el buscador de pacientes solo
 * conocía las fichas más recientes: con una antigua salía «Selecciona
 * paciente…» aunque el paciente ya estaba puesto.
 *
 * `onCreated(ultimo, creados)`: lo creado, para que la ficha lo deje marcado
 * para la tablet.
 */
export function NewConsentDialog({ open, onOpenChange, onCreated, initialPatient, pacienteFijo }) {
  const idFijo = pacienteFijo?.id || '';
  const [patients, setPatients] = useState([]);
  const [surgeries, setSurgeries] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [surgeons, setSurgeons] = useState([]);
  const [patient_id, setPatientId] = useState(idFijo || initialPatient || '');
  // Selección MÚLTIPLE de plantillas (2026-08-27, dirección): marcar varias y
  // generarlas todas de golpe, en vez de una a una volviendo al desplegable.
  const [template_codes, setTemplateCodes] = useState(() => new Set());
  const [generando, setGenerando] = useState(false);
  const [surgery_code, setSurgeryCode] = useState('');  // opcional
  // Phase 23.11 — fecha del consentimiento (por defecto: hoy + 2 días, simulando D-2 de una cirugía hipotética)
  // El usuario puede ajustarla a una fecha concreta antes de generar.
  const [doc_date, setDocDate] = useState('');
  const [surgery_date, setSurgeryDate] = useState('');
  const [surgeon_id, setSurgeonId] = useState('');
  // Si quien está delante es médico, el documento sale a SU nombre y el
  // desplegable no se toca (dirección, 21/9: «administrativas y administradores
  // también pueden crear consentimientos a nombre de quien quieran; los que no
  // pueden son los doctores, sale cada uno a su nombre»). La regla la hace
  // cumplir el servidor —devuelve 403—; aquí solo se evita ofrecer algo que
  // luego se va a rechazar.
  const [miFicha, setMiFicha] = useState(null);
  const { user } = useAuth();
  const soyMedico = ['doctor', 'medico'].includes((user?.role || '').toLowerCase()) && !!miFicha;
  // El titular sale de la lista de abajo (no se ofrece dos veces), pero su id
  // hace falta para poder PEDIRLO: desde que el servidor pone al médico que
  // crea el documento, dejar el hueco vacío ya no significa «el titular».
  // El titular es el cirujano marcado por defecto (`is_default`) en el
  // catálogo; antes se buscaba primero por un código escrito aquí (V2).
  const titularId = useMemo(() => surgeons.find(s => s.is_default)?.id || '', [surgeons]);
  // EL FORMATO DEL PAPEL, ELEGIDO A MANO (administración, 27/10/2026): los
  // formatos son las marcas propias de Configuración → Marcas (el servidor
  // las da; aquí no hay ninguno escrito). Se propone el del médico elegido
  // —el titular, el de Configuración; otro médico, el de su marca— y se
  // puede cambiar antes de generar, también si quien está delante es médico.
  // Una vez tocado a mano, cambiar de médico ya no lo vuelve a proponer.
  const [formatos, setFormatos] = useState([]);
  const [formato_clave, setFormatoClave] = useState('');
  const formatoTocado = useRef(false);
  const aplicarPropuestaDeFormato = useCallback((datos) => {
    if (Array.isArray(datos?.formatos)) setFormatos(datos.formatos);
    if (!formatoTocado.current) setFormatoClave(datos?.formato_clave || datos?.formato_propuesto || '');
  }, []);

  useEffect(() => {
    if (open) {
      formatoTocado.current = false;
      setFormatoClave('');
      // Con el paciente fijado no hay buscador: no hace falta la lista.
      if (!idFijo) api.get('/api/patients').then(r => setPatients(r.data));
      api.get('/api/surgeries').then(r => setSurgeries(r.data));
      // Solo las activas (20/10/2026): una desactivada se retiró (es lo que
      // se hace en vez de borrarla) y un borrador importado del Drive no se da
      // a firmar hasta que lo revisa el médico y lo activa administración.
      api.get('/api/consent-templates').then(r => setTemplates(
        (Array.isArray(r.data) ? r.data : []).filter(t => t.active !== false)));
      Promise.all([
        api.get('/api/surgeons'),
        // Quién es el médico que entra: lo dice el servidor, que es quien sabe
        // casar el usuario con su ficha del catálogo. Va EN PARALELO y se
        // espera a las dos: si se diera por bueno el titular mientras esta
        // tarda, la pantalla diría una cosa y el papel saldría con otra.
        api.get('/api/consents/mi-medico').catch(() => ({ data: { ficha: null } })),
      ]).then(([r, m]) => {
        const list = r.data || [];
        setSurgeons(list);
        // Por defecto, EL MÉDICO QUE LO ESTÁ HACIENDO (dirección, 21/9/2026:
        // «cada uno directamente sale a su nombre»). Si no lo es —recepción,
        // dirección—, el titular, como hasta ahora; y si no se pudo preguntar,
        // el titular también: nunca se adivina un médico.
        const titular = list.find(s => s.is_default);
        const mia = m?.data?.ficha || null;
        setMiFicha(mia);
        const elegido = (mia && list.find(s => s.id === mia.id)) || titular;
        if (elegido) setSurgeonId(elegido.id);
      }).catch(() => setSurgeons([]));
      setPatientId(idFijo || initialPatient || '');
      setSurgeryCode('');
      setTemplateCodes(new Set());
      setGenerando(false);
      // Por defecto, doc_date = hoy. El usuario lo puede cambiar manualmente.
      const today = new Date().toISOString().slice(0, 10);
      setDocDate(today);
      setSurgeryDate('');
    }
  }, [open, initialPatient, idFijo]);

  // EL MÉDICO DEL PACIENTE, PROPUESTO (revisión del 20/10/2026). El
  // consentimiento nacía a nombre del titular aunque el paciente fuera de otro
  // médico. Ahora, para quien prepara el papeleo de toda la clínica (no para
  // un médico, que emite siempre a su nombre), el desplegable empieza en el
  // médico de la PRÓXIMA cita del paciente; si no tiene, el de la última; y si
  // no, el titular. Lo calcula el servidor (`/api/consents/medico-propuesto`).
  // Es solo la propuesta: se cambia en el desplegable, y lo que se guarda es
  // lo elegido (se manda `surgeon_id` explícito).
  const [propuesta, setPropuesta] = useState(null);
  useEffect(() => {
    if (!open || !patient_id || soyMedico || !surgeons.length) {
      setPropuesta(null);
      return undefined;
    }
    let vigente = true;
    api.get('/api/consents/medico-propuesto', { params: { patient_id } })
      .then((r) => {
        if (!vigente) return;
        const ficha = r?.data?.ficha;
        if (ficha && surgeons.some((s) => s.id === ficha.id)) {
          setPropuesta(r.data);
          setSurgeonId(ficha.id);
          // El formato propuesto es el de ESE médico: solo si de verdad
          // queda elegido (revisión del 27/10/2026).
          aplicarPropuestaDeFormato(r?.data);
        } else {
          setPropuesta(null);
          aplicarPropuestaDeFormato({ formatos: r?.data?.formatos });
        }
      })
      // Si no se puede preguntar, se queda lo de siempre (el titular): nunca
      // se adivina un médico.
      .catch(() => { if (vigente) setPropuesta(null); });
    return () => { vigente = false; };
  }, [open, patient_id, soyMedico, surgeons, aplicarPropuestaDeFormato]);

  // El formato propuesto para el médico elegido, cada vez que cambia (también
  // para un médico, al que no se le propone nadie pero sí un formato). Si no
  // se puede preguntar, se queda lo que haya: nunca se adivina un formato.
  useEffect(() => {
    if (!open || !surgeons.length) return undefined;
    let vigente = true;
    api.get('/api/consents/formato-propuesto', { params: { surgeon_id: surgeon_id || '' } })
      .then((r) => { if (vigente) aplicarPropuestaDeFormato(r?.data); })
      .catch(() => {});
    return () => { vigente = false; };
  }, [open, surgeon_id, surgeons, aplicarPropuestaDeFormato]);

  // Cuando el usuario fija la fecha de cirugía, calculamos automáticamente
  // doc_date = surgery_date − 48h (regla del centro).
  const onSurgeryDateChange = (val) => {
    setSurgeryDate(val);
    if (val) {
      const d = new Date(val + 'T00:00:00');
      d.setDate(d.getDate() - 2);
      setDocDate(d.toISOString().slice(0, 10));
    }
  };

  // Marcar/desmarcar una plantilla. Al MARCAR, si está asociada a alguna
  // cirugía del catálogo y aún no hay cirugía vinculada, se sugiere.
  const onTemplateToggle = (code) => {
    setTemplateCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
        if (!surgery_code) {
          const matchingSurgery = surgeries.find(s => s.consent_template_code === code);
          if (matchingSurgery) setSurgeryCode(matchingSurgery.code);
        }
      }
      return next;
    });
  };

  const submit = async () => {
    if (generando) return;
    if (!patient_id) return toast.error('Selecciona un paciente del desplegable');
    const codigos = Array.from(template_codes);
    if (!codigos.length) return toast.error('Marca al menos un consentimiento');
    // El backend valida que el paciente existe (404 si no). NO comprobamos
    // contra la lista local `patients`: el buscador trae pacientes por
    // búsqueda en servidor que pueden no estar entre los 200 más recientes
    // cargados aquí (hay miles), y ese guard bloqueaba generar consentimientos
    // a pacientes antiguos (2026-07-17).
    setGenerando(true);
    const creados = [];
    let ultimo = null;
    try {
      for (const code of codigos) {
        // El paciente fijado manda en TODOS los de la tanda (21/10/2026).
        const payload = { patient_id: idFijo || patient_id, template_code: code };
        if (surgery_code) payload.surgery_code = surgery_code;
        if (doc_date) payload.doc_date = doc_date;
        if (surgery_date) payload.surgery_date = surgery_date;
        if (surgeon_id && code !== 'anestesia') payload.surgeon_id = surgeon_id;
        // El formato elegido, en todos (también en el de anestesia, que no
        // lleva médico pero sí formato). Sin formatos no se manda nada.
        if (formatos.length) payload.formato_clave = formato_clave || '';
        const res = await api.post('/api/consents', payload);
        creados.push(res.data);
        ultimo = res.data;
      }
      toast.success(creados.length === 1
        ? `Consentimiento "${creados[0].template_name}" creado para ${creados[0].patient_name || 'el paciente'}`
        : `${creados.length} consentimientos creados para ${ultimo?.patient_name || 'el paciente'}`);
      onCreated && onCreated(ultimo, creados);
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Error desconocido';
      if (creados.length) {
        toast.warning(`Se crearon ${creados.length} de ${codigos.length}; falló el siguiente: ${detail}`);
        onCreated && onCreated(ultimo, creados);
        onOpenChange(false);
      } else {
        toast.error(`Error al crear consentimiento: ${detail}`);
      }
      logError('Consent create error:', err?.response?.data, 'payload:', { patient_id, codigos, surgery_code });
    } finally {
      setGenerando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Generar consentimiento informado</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Paciente *</Label>
            {idFijo ? (
              // Desde su ficha: el paciente es ese y no se cambia aquí.
              <div className="mt-1 rounded-md border border-border bg-[rgba(46,110,94,0.05)] px-3 py-2 text-sm"
                data-testid="consent-patient-fijo">
                <div className="font-medium">
                  {fullName(pacienteFijo) || pacienteFijo.name || 'Paciente'}
                  {pacienteFijo.nif ? ` · ${pacienteFijo.nif}` : ''}
                </div>
                <div className="text-xs text-muted-foreground">
                  Desde su ficha: todos los que generes ahora son para este paciente.
                </div>
              </div>
            ) : (
              <PatientCombobox value={patient_id} onChange={setPatientId} patients={patients} dataTestid="consent-patient-select" />
            )}
          </div>

          <div>
            <Label>Tipo de consentimiento * <span className="font-normal text-muted-foreground">(marca todos los que necesites)</span></Label>
            <div className="mt-1 max-h-56 overflow-y-auto rounded-md border border-border divide-y divide-border" data-testid="consent-template-checklist">
              {templates.map(t => (
                <label
                  key={t.code}
                  className={`flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer hover:bg-[rgba(46,110,94,0.05)] ${template_codes.has(t.code) ? 'bg-[rgba(46,110,94,0.08)]' : ''}`}
                  data-testid={`consent-template-${t.code}`}
                >
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-[#2E6E5E] shrink-0"
                    checked={template_codes.has(t.code)}
                    onChange={() => onTemplateToggle(t.code)}
                  />
                  <span className="inline-flex items-center gap-1.5">
                    <IconoConsentimiento code={t.code} />
                    {t.name}
                  </span>
                </label>
              ))}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {template_codes.size === 0
                ? 'Marca uno o varios (p. ej. el de la cirugía + anestesia + imágenes) y se generan todos de golpe.'
                : `${template_codes.size} seleccionado${template_codes.size === 1 ? '' : 's'}.`}
            </div>
          </div>

          <div>
            <Label>Cirugía vinculada (opcional)</Label>
            <Select value={surgery_code || 'none'} onValueChange={(v) => setSurgeryCode(v === 'none' ? '' : v)}>
              <SelectTrigger data-testid="consent-surgery-select"><SelectValue placeholder="(Sin vincular a cirugía concreta)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Sin vincular —</SelectItem>
                {surgeries.map(s => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="text-xs text-muted-foreground mt-1">Útil solo si quieres dejar registro de a qué cirugía corresponde el consentimiento.</div>
          </div>

          {/* Phase 23.11 — Fecha de cirugía + fecha del consentimiento (D-2) + doctor */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="surgery-date-input">Fecha de cirugía (opcional)</Label>
              <Input
                id="surgery-date-input"
                type="date"
                value={surgery_date}
                onChange={(e) => onSurgeryDateChange(e.target.value)}
                data-testid="consent-surgery-date-input"
              />
              <div className="text-xs text-muted-foreground mt-1">Al fijarla, la fecha del consent se ajusta a 48h antes.</div>
            </div>
            <div>
              <Label htmlFor="doc-date-input">Fecha del consentimiento *</Label>
              <Input
                id="doc-date-input"
                type="date"
                value={doc_date}
                onChange={(e) => setDocDate(e.target.value)}
                data-testid="consent-doc-date-input"
              />
              <div className="text-xs text-muted-foreground mt-1">Default: cirugía − 48h. Editable.</div>
            </div>
          </div>

          <div>
            <Label>Doctor que firma</Label>
            {/* El titular se enseña como «Cirujano titular»: no tiene fila
                propia en la lista y, con su id puesto, el desplegable salía
                en blanco (visto al abrir el diálogo desde la ficha, 21/10/2026).
                Lo que se manda sigue siendo su id. */}
            <Select value={!surgeon_id || surgeon_id === titularId ? 'default' : surgeon_id} disabled={soyMedico}
                    onValueChange={(v) => setSurgeonId(v === 'default' ? (titularId || '') : v)}>
              <SelectTrigger data-testid="consent-surgeon-select">
                <SelectValue placeholder="Cirujano titular (por defecto)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Cirujano titular</SelectItem>
                {surgeons.filter(s => s.id !== titularId).map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name} · {etiquetaColegiado(s.license)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-xs text-muted-foreground mt-1" data-testid="consent-surgeon-nota">
              {soyMedico
                ? `Sale a tu nombre (${miFicha.name}${miFicha.license ? ` · Colegiado ${miFicha.license}` : ''}). Para emitirlo a nombre de otro médico, lo prepara administración.`
                : 'Sale a nombre del médico que se elija. Solo el cirujano titular lleva firma+sello automático; el resto lo firma desde la ficha del paciente, después de que firme el paciente.'}
            </div>
            {!soyMedico && propuesta?.ficha && (
              <div className="text-xs text-[#2E6E5E] mt-1" data-testid="consent-surgeon-propuesta">
                {propuesta.origen === 'proxima_cita' || propuesta.origen === 'ultima_cita'
                  ? `Propuesto: ${propuesta.ficha.name}, el médico de su ${propuesta.origen === 'proxima_cita' ? 'próxima' : 'última'} cita${propuesta.fecha ? ` (${formatDate(propuesta.fecha)})` : ''}. Puedes cambiarlo.`
                  : 'Propuesto: el cirujano titular (el paciente no tiene citas con un médico del Catálogo QX). Puedes cambiarlo.'}
              </div>
            )}
          </div>

          {formatos.length > 0 && (
            <div>
              <Label>Formato</Label>
              <Select value={formato_clave || ''} onValueChange={(v) => { formatoTocado.current = true; setFormatoClave(v); }}>
                <SelectTrigger data-testid="consent-formato">
                  <SelectValue placeholder="Elegir formato…" />
                </SelectTrigger>
                <SelectContent>
                  {formatos.map((f) => <SelectItem key={f.clave} value={f.clave}>{f.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-xs text-muted-foreground mt-1" data-testid="consent-formato-pista">
                {formatoTocado.current
                  ? 'Elegido a mano.'
                  : 'Propuesto por el médico; puedes cambiarlo antes de generar.'}
              </div>
            </div>
          )}

          <div className="text-xs text-muted-foreground bg-[rgba(46,110,94,0.08)] p-3 rounded">
            <strong>Importante:</strong> El consentimiento se generará a nombre del doctor seleccionado (con su firma, si es el titular). La regla del centro es que se firme 48h antes de la cirugía — al fijar la fecha de cirugía, la fecha del consent se ajusta automáticamente.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={generando} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="consent-create-button">
            <Plus className="w-4 h-4 mr-2" />
            {generando ? 'Generando…' : template_codes.size > 1 ? `Crear ${template_codes.size} consentimientos` : 'Crear'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ConsentsPage() {
  const [searchParams] = useSearchParams();
  const initialPatient = searchParams.get('patient');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(!!initialPatient);
  const [preview, setPreview] = useState({ open: false, url: '', filename: '', title: '' });
  const [q, setQ] = useState('');
  const [templateFilter, setTemplateFilter] = useState('all');

  const fetchItems = async () => {
    setLoading(true);
    try { const r = await api.get('/api/consents'); setItems(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchItems(); }, []);
  // Un consentimiento FIRMADO no se borra (23/9/2026): el servidor lo rechaza
  // con 409 y aquí la papelera ni se ofrece (ver `tieneFirma`). Si aun así
  // dice que no —alguien firmó mientras tanto—, se enseña su motivo: antes el
  // rechazo se perdía sin aviso y la lista no se refrescaba.
  const onDelete = async (c) => {
    // El mismo aviso que la papelera de la ficha (21/10/2026): qué se borra,
    // que no tiene vuelta atrás y que lo mandado a firmar se anula con él.
    if (!window.confirm(`¿Borrar el consentimiento «${c.template_name || 'sin nombre'}» de `
      + `${c.patient_name || 'este paciente'}? No se puede deshacer.\n\n`
      + 'Si se le había mandado a firmar (tablet o enlace de casa), se anula también.')) return;
    try {
      const r = await api.delete(`/api/consents/${c.id}`);
      toast.success(`Consentimiento borrado${textoAnulados(r?.data?.anulados)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se ha podido eliminar el consentimiento');
    }
    fetchItems();
  };

  // Phase 23.15 — Cola de la tablet: contador de pendings + botón "Limpiar cola"
  // para destrabar el iPad cuando hay consents olvidados.
  //
  // SIGUE AQUÍ, y no en la ficha, a propósito (administración, 18/9/2026): «Limpiar
  // cola» cancela lo de TODOS los pacientes a la vez. Es la palanca de cuando
  // el iPad está atascado con lo de alguien que ya no está y no sabes quién
  // es —justo cuando no puedes abrir su ficha, porque no sabes cuál—. Lo que
  // sí se fue a la ficha es lo de un paciente concreto: ver qué tiene en la
  // cola y quitarlo uno a uno.
  //
  // `estado` en vez de un número pelado: si el servidor no contesta, esto
  // ponía «Cola tablet (0)» —un error disfrazado de dato, y encima el dato
  // tranquilizador—. Ahora lo dice.
  //
  // Y EL NÚMERO LO CUENTA EL SERVIDOR (21/9/2026). Aquí se contaban los
  // 'pending' de los 100 items que devuelve /queue, y eso mentía por los dos
  // lados: por arriba metía en la cuenta lo que el paciente está firmando
  // desde casa —que el iPad no enseña y este botón no puede cancelar: el
  // contador subía solo y el botón contestaba «0 canceladas» en verde—, y por
  // abajo, en cuanto había 100 entradas más nuevas (la colección guarda
  // también las firmadas de siempre), un pendiente viejo desaparecía de la
  // cuenta y la pantalla decía «Cola tablet (0)» con el iPad ocupado.
  // `pendientes_en_tablet` viene contado en servidor, sin límite y con la
  // misma regla que usa la tablet para servir.
  const [tabletQueue, setTabletQueue] = useState({ estado: 'cargando', pending: 0, total: 0 });

  const refreshTabletQueue = async () => {
    try {
      const r = await api.get('/api/consent-tablet/queue');
      const items = r.data?.items || [];
      // El `??` es para el rato del despliegue (servidor viejo sin el campo).
      const pending = r.data?.pendientes_en_tablet
        ?? items.filter(x => x.status === 'pending').length;
      setTabletQueue({ estado: 'ok', pending, total: items.length });
      return pending;
    } catch (e) {
      setTabletQueue({ estado: 'error', pending: 0, total: 0,
                       detalle: e?.response?.data?.detail || 'sin respuesta' });
      return null;
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refreshTabletQueue(); }, []);

  const onClearTabletQueue = async () => {
    // Refresca primero por si el contador local está desactualizado. Si la
    // consulta falla NO se sigue: un «ya está vacía» que sale de un error no
    // es una respuesta.
    const pendingNow = await refreshTabletQueue();
    if (pendingNow === null) {
      toast.error('No se ha podido consultar la cola de la tablet, así que no se sabe qué hay dentro. Vuelve a intentarlo.');
      return;
    }
    if (pendingNow === 0) {
      toast.info('La cola de la tablet ya está vacía. Los consents que envíes ahora deberían aparecer en la tablet en ≤4 segundos.');
      return;
    }
    if (!window.confirm(
      `Esto vacía la cola de la tablet: ${pendingNow} consentimiento(s) dejan de estar en el iPad. ` +
      'Los que solo estaban ahí habrá que volver a mandarlos; los que además tienen enlace de ' +
      'firma siguen firmándose desde casa. ¿Continuar?'
    )) return;
    try {
      const r = await api.delete('/api/consent-tablet/clear-pending');
      const canceladas = r.data?.cancelled || 0;
      const quitadas = r.data?.quitados_de_la_tablet || 0;
      const hechas = r.data?.total ?? (canceladas + quitadas);
      if (hechas === 0) {
        // Un «hecho» en verde con 0 encima enseña a no leer los avisos. Si el
        // botón no ha alcanzado nada de lo que decía haber, eso no es un
        // éxito: es que hay algo ahí dentro que este botón no toca.
        toast.warning(`La cola decía ${pendingNow} y no se ha podido quitar ninguna. `
          + 'Vuelve a mirar la cola: puede que ya no estuvieran, o que sean de algo que este botón no alcanza.');
      } else {
        toast.success(`Cola de la tablet limpiada · ${hechas} fuera del iPad`
          + (quitadas ? ` (${quitadas} siguen firmables desde el enlace del paciente)` : ''));
      }
      await refreshTabletQueue();
    } catch (e) {
      toast.error(`Error al limpiar la cola: ${e?.response?.data?.detail || 'desconocido'}`);
    }
  };

  // EL ENVÍO A LA TABLET YA NO SE HACE DESDE AQUÍ (administración, 18/9/2026).
  // Esta pantalla es la lista de TODA la clínica: para mandarle el iPad a una
  // paciente había que buscarla entre miles de filas y marcar las suyas, que
  // es donde se marca la de otra. El envío vive ahora en la pestaña
  // «Consentimientos» de SU ficha, donde solo están los suyos
  // (components/patient/PatientConsentsTab.jsx). Aquí queda el buscador.

  // Listar plantillas para filtro
  const visible = useMemo(() => {
    let arr = items;
    if (templateFilter !== 'all') arr = arr.filter(c => c.template_code === templateFilter);
    if (q) {
      const qq = normalizaTexto(q);
      arr = arr.filter(c => normalizaTexto(c.patient_name).includes(qq) || normalizaTexto(c.template_name).includes(qq));
    }
    return arr;
  }, [items, q, templateFilter]);

  const templateCodes = useMemo(() => {
    const set = new Map();
    items.forEach(c => { if (c.template_code) set.set(c.template_code, c.template_name); });
    return Array.from(set, ([code, name]) => ({ code, name }));
  }, [items]);

  return (
    <div>
      <PageHeader title="Consentimientos informados" subtitle={`${items.length} consentimientos generados · buscador de toda la clínica (el envío a la tablet se hace desde la ficha del paciente)`}
        actions={
          <>
          {/* Las plantillas se gestionan desde aquí (2026-07-27): se quitaron
              del menú lateral porque su sitio natural es esta pantalla. */}
          <Button variant="outline" asChild data-testid="consents-plantillas-link">
            <Link to="/plantillas-consentimientos">
              <FileCog className="w-4 h-4 mr-2" /> Plantillas
            </Link>
          </Button>
          <Button
            variant="outline"
            onClick={() => { window.location.href = '/firma-consent'; }}
            title="Pasa esta pantalla al entorno de firma para el paciente (pantalla completa, sin menús)"
            data-testid="consents-modo-tablet"
          >
            <Tablet className="w-4 h-4 mr-2" /> Modo tablet
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={onClearTabletQueue}
              className={tabletQueue.estado === 'error'
                ? "border-red-300 text-red-700 hover:bg-red-50"
                : tabletQueue.pending > 0
                  ? "border-amber-300 text-amber-700 hover:bg-amber-50"
                  : "border-border text-muted-foreground hover:bg-muted"}
              title={tabletQueue.estado === 'error'
                ? `No se ha podido consultar la cola de la tablet (${tabletQueue.detalle}) · pulsa para reintentar`
                : tabletQueue.estado === 'cargando'
                  ? 'Consultando la cola de la tablet…'
                  : tabletQueue.pending > 0
                    ? `${tabletQueue.pending} consentimientos esperando firma EN EL IPAD · pulsa para sacarlos de la cola`
                    : "Cola de la tablet vacía · pulsa para refrescar"}
              data-testid="consent-clear-tablet-queue"
            >
              <Trash className="w-4 h-4 mr-2" />
              {tabletQueue.estado === 'error'
                ? 'Cola tablet: sin respuesta'
                : tabletQueue.estado === 'cargando'
                  ? 'Cola tablet…'
                  : tabletQueue.pending > 0
                    ? `Limpiar cola tablet (${tabletQueue.pending})`
                    : 'Cola tablet (0)'}
            </Button>
            <Button onClick={() => setOpen(true)} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="consent-new-button">
              <Plus className="w-4 h-4 mr-2" /> Generar consentimiento
            </Button>
          </div>
          </>
        } />

      <div className="cjo-card p-4 mb-3 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Buscar paciente o plantilla…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="consent-search" />
        </div>
        <Select value={templateFilter} onValueChange={setTemplateFilter}>
          <SelectTrigger className="w-full sm:w-[260px]" data-testid="consent-template-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las plantillas</SelectItem>
            {templateCodes.map(t => <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="cjo-card overflow-hidden">
        {loading ? <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
          : visible.length === 0 ? <EmptyState icon={FilePen} title="Sin consentimientos" description="Genera el primero." />
          : <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-[rgba(46,110,94,0.04)] text-left">
              <tr>
                <th className="py-3 px-4 font-semibold text-[#13231E]">Paciente</th>
                <th className="py-3 px-4 font-semibold text-[#13231E]">Plantilla</th>
                <th className="py-3 px-4 font-semibold text-[#13231E]">Cirugía vinculada</th>
                <th className="py-3 px-4 font-semibold text-[#13231E]">Creado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visible.map(c => (
                <tr key={c.id} className="border-t border-border hover:bg-[rgba(46,110,94,0.03)]" data-testid="consent-row" data-consent-id={c.id}>
                  <td className="py-3 px-4 font-medium">
                    {/* El nombre lleva a SU ficha, que es donde se manda ahora
                        el iPad (administración, 18/9/2026). Buscar aquí y abrir la
                        ficha es justo para lo que sirve esta pantalla. */}
                    {c.patient_id ? (
                      <Link to={`/pacientes/${c.patient_id}`} className="text-[#2E6E5E] hover:underline" data-testid="consent-patient-link">
                        {c.patient_name}
                      </Link>
                    ) : c.patient_name}
                  </td>
                  <td className="py-3 px-4">
                    <span className="inline-flex items-center gap-1">
                      <IconoConsentimiento code={c.template_code} />
                      <span className="text-xs">{c.template_name}</span>
                    </span>
                  </td>
                  <td className="py-3 px-4 text-xs text-muted-foreground">{c.surgery_name || '—'}</td>
                  <td className="py-3 px-4 text-xs">{formatDate(c.created_at)}</td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex gap-1 items-center justify-end">
                      {c.patient_signature_b64 ? (
                        <span className="inline-flex items-center text-xs text-green-700 font-medium mr-2" title="Paciente firmó en la tablet">
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Firmado
                        </span>
                      ) : (
                        <span className="text-xs text-amber-700 mr-2" data-testid="consent-pendiente">Pendiente de firma</span>
                      )}
                      <Button size="sm" variant="outline" onClick={() => setPreview({ open: true, url: `${BACKEND_URL}/api/consents/${c.id}/pdf`, filename: `consentimiento_${c.id}.pdf`, title: `Consentimiento · ${c.template_name} · ${c.patient_name}` })} data-testid="consent-pdf-button">
                        <Eye className="w-4 h-4" />
                      </Button>
                      {/* La papelera, solo si NADIE ha firmado: ni la paciente,
                          ni la doctora desde la ficha, ni el anestesista desde
                          Quirófano (23/9/2026). Lo firmado se conserva tal
                          cual; el servidor también lo impide. */}
                      {!tieneFirma(c) && (
                        <Button size="sm" variant="ghost" onClick={() => onDelete(c)} className="text-destructive" data-testid={`consent-delete-${c.id}`} title="Eliminar (no está firmado)"><Trash2 className="w-3.5 h-3.5" /></Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>}
      </div>
      <NewConsentDialog open={open} onOpenChange={setOpen} initialPatient={initialPatient} onCreated={fetchItems} />
      <PdfPreviewDialog open={preview.open} onOpenChange={(v) => setPreview(s => ({ ...s, open: v }))} url={preview.url} filename={preview.filename} title={preview.title} />
    </div>
  );
}
